import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { ModalController } from "ionic-angular";
import { CompanyTeamsService } from "providers/company-teams-service/company-teams-service";
import { Subscription } from "rxjs";

/**
 * OPS-2873. The Company -> Work Force -> Multi-Site Team tab.
 *
 * A company has exactly ONE of these, or none. So this is not a grid: it is an empty state with an
 * Add control, or the team with an Edit control. Once the team exists the Add control is gone for
 * good - the team is renamed, never replaced. The server enforces the same rule, because hiding a
 * control is a hint and not a gate (CO-3511).
 *
 * Creating and editing reuse CompanyTeamsModalPage, the same modal Location Teams and Company Teams
 * drive. It is told `multiSiteTeam: true`, which is what makes it skip the required-role validation
 * and stamp `companyTeamType: "MTEAM"` on the payload.
 *
 * Deliberately absent: Delete (there is no delete permission), and any patient list - a company org
 * has no patients, so a Multi-Site Team can never be assigned to one.
 */
@Component({
  selector: "multi-site-team",
  templateUrl: "multi-site-team.html",
})
export class MultiSiteTeamComponent implements OnInit, OnDestroy {
  static readonly LOAD_FAILED_MESSAGE =
    "Unable to load the Multi-Site Team. Please try again.";

  /** Company:Workforce:MultiSiteTeam:ADD (or :*), resolved by the parent page. */
  @Input() canAdd: boolean = false;

  /** Company:Workforce:MultiSiteTeam:EDIT (or :*), resolved by the parent page. */
  @Input() canEdit: boolean = false;

  /** The team, or null when none has been created yet. */
  team: any = null;

  /** Role rows from the server, used to build the modal's form. */
  roles: any[] = [];

  loading: boolean = true;

  /**
   * True when the read FAILED, as opposed to succeeding with no team. The two look identical on
   * screen otherwise, and the difference matters: one invites you to create a team, the other means
   * we do not know whether one exists. The same distinction CompanyTeamsPage draws.
   */
  loadFailed: boolean = false;

  /**
   * Members grouped by role, for display.
   *
   * A FIELD refreshed explicitly, NOT a getter. A getter driving *ngFor returns freshly constructed
   * objects on every change-detection pass, so Angular destroys and recreates every row each time -
   * the bug OPS-2858 hit in the org picker, where a click never landed on the element it started
   * on. Worth remembering as a general Angular trap, not a support-user one.
   */
  memberRows: any[] = [];

  readonly loadFailedMessage = MultiSiteTeamComponent.LOAD_FAILED_MESSAGE;

  private subscriptions: Subscription[] = [];

  constructor(
    private companyTeamsService: CompanyTeamsService,
    private modalCtrl: ModalController
  ) {}

  ngOnInit(): void {
    this.loadRoles();
    this.loadTeam();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  /** True only once we KNOW there is no team - never while loading or after a failure. */
  get showEmptyState(): boolean {
    return !this.loading && !this.loadFailed && !this.team;
  }

  get showAddButton(): boolean {
    return this.showEmptyState && this.canAdd;
  }

  get showEditButton(): boolean {
    return !!this.team && this.canEdit;
  }

  /** True when the team exists but nobody has been added yet - a legitimate state here. */
  get hasNoMembers(): boolean {
    return !!this.team && this.memberRows.length === 0;
  }

  loadTeam(): void {
    this.loading = true;
    this.loadFailed = false;
    this.subscriptions.push(
      this.companyTeamsService.getMultiSiteTeam().subscribe(
        (data: any) => {
          // An empty body means "no team yet" - a 200, not a 404.
          this.team = data && data.id ? data : null;
          this.refreshMemberRows();
          this.loading = false;
        },
        () => {
          this.team = null;
          this.refreshMemberRows();
          this.loadFailed = true;
          this.loading = false;
        }
      )
    );
  }

  trackByRoleName(index: number, row: any): string {
    return row ? row.roleName : String(index);
  }

  addTeam(): void {
    // Handler-level guard, not just the *ngIf - CO-3511.
    if (!this.showAddButton) {
      return;
    }
    this.openModal("new", { name: "", roles: this.rolesForModal() });
  }

  editTeam(): void {
    if (!this.showEditButton) {
      return;
    }
    this.openModal("edit", {
      id: this.team.id,
      name: this.team.name,
      roles: this.rolesForModal(this.team.members),
    });
  }

  private refreshMemberRows(): void {
    if (!this.team || !this.team.members) {
      this.memberRows = [];
      return;
    }
    const byRole = {};
    this.team.members.forEach((member) => {
      const roleName = member.role ? member.role.name : "";
      if (!byRole[roleName]) {
        byRole[roleName] = { roleName, members: [] };
      }
      byRole[roleName].members.push(member);
    });
    this.memberRows = Object.keys(byRole)
      .sort()
      .map((key) => byRole[key]);
  }

  private loadRoles(): void {
    this.subscriptions.push(
      this.companyTeamsService.getCompanyTeamRoles().subscribe(
        (data: any) => {
          this.roles = data || [];
        },
        () => {
          this.roles = [];
        }
      )
    );
  }

  /**
   * Fresh role rows every time the modal opens, with existing members slotted in.
   *
   * The modal mutates what it is given, so handing it this component's own array would leave edits
   * on screen after a cancel. Every role needs a `members` array carrying at least one blank slot -
   * that is the shape the modal's role loop expects.
   */
  private rolesForModal(members: any[] = []): any[] {
    return this.roles.map((role) => {
      const roleMembers = (members || [])
        .filter((m) => m.role && m.role.id === role.id)
        .map((m) => ({
          id: m.personId,
          personId: m.personId,
          firstName: m.firstName,
          lastName: m.lastName,
        }));
      return Object.assign({}, role, {
        members: roleMembers.length
          ? roleMembers
          : [{ id: null, personId: null, firstName: "", lastName: "" }],
        membersByRole: [],
        loaded: false,
        loading: false,
      });
    });
  }

  private openModal(mode: string, companyTeam: any): void {
    const modal = this.modalCtrl.create(
      "CompanyTeamsModalPage",
      {
        mode,
        companyTeam,
        // OPS-2873: the switch that turns off required-role validation and stamps
        // companyTeamType = MTEAM on the payload.
        multiSiteTeam: true,
        palliativeCareTeamToggle: false,
      },
      { cssClass: "company-teams-modal" }
    );
    modal.onDidDismiss((data: any) => {
      if (data && data.update) {
        this.loadTeam();
      }
    });
    modal.present();
  }
}



// WEBPACK FOOTER //
// ./src/components/system-admin/workforce/multi-site-team/multi-site-team.ts